import React, { useMemo } from "react";
import { GebrekenImageGallery } from "./GebrekenImageGallery";
import type { Bevinding } from "@/features/gebreken/types/bevinding";
import type { ProjectImage } from "@/features/gebreken/hooks/useProjectImages";

interface GebrekenTableRowProps {
  bevinding: Bevinding;
  index: number;
  images: ProjectImage[];
}

/**
 * Table row component for displaying a single bevinding (defect) in the gebreken table.
 * Matches the style of other table rows in the markdown editor.
 */
export const GebrekenTableRow: React.FC<GebrekenTableRowProps> = ({
  bevinding,
  index,
  images,
}) => {
  // Get images for this bevinding
  const bevindingImages = useMemo(() => {
    if (!bevinding.afbeeldingen || bevinding.afbeeldingen.length === 0) {
      return [];
    }
    return bevinding.afbeeldingen
      .map((fileName) => images.find((img) => img.name === fileName))
      .filter((img): img is ProjectImage => img !== undefined);
  }, [bevinding.afbeeldingen, images]);

  return (
    <tr>
      {/* Number column */}
      <td className="border border-border px-3 py-2 text-left align-top">
        <p className="m-0 p-0 leading-relaxed">{index + 1}</p>
      </td>

      {/* Gebrek column */}
      <td className="border border-border px-3 py-2 text-left align-top">
        <p className="m-0 p-0 leading-relaxed">{bevinding.gebrek}</p>
      </td>

      {/* Maatregel column */}
      <td className="border border-border px-3 py-2 text-left align-top">
        {bevinding.maatregel ? (
          <p className="m-0 p-0 leading-relaxed">{bevinding.maatregel}</p>
        ) : (
          <p className="m-0 p-0 leading-relaxed text-muted-foreground italic">
            Geen maatregel opgegeven
          </p>
        )}
      </td>

      {/* Voorschrift column */}
      <td className="border border-border px-3 py-2 text-left align-top">
        {bevinding.voorschrift && bevinding.voorschrift.length > 0 ? (
          <p className="m-0 p-0 leading-relaxed">
            {bevinding.voorschrift.join(", ")}
          </p>
        ) : (
          <p className="m-0 p-0 leading-relaxed text-muted-foreground italic">
            Geen
          </p>
        )}
      </td>

      {/* Foto's column */}
      <td className="border border-border px-3 py-2 text-left align-top">
        {bevindingImages.length > 0 ? (
          <GebrekenImageGallery
            images={bevindingImages}
            bevindingId={bevinding.id}
          />
        ) : (
          <p className="m-0 p-0 leading-relaxed text-muted-foreground italic">
            Geen foto's
          </p>
        )}
      </td>
    </tr>
  );
};
